import * as React from "react";
import DurationBar from "./DurationBar";
import TrimmingArea from "./TrimmingArea";
import ResizeArea from "./ResizeArea";
import { Crop, AspectRatio, PlayerButtonType } from "core/models";
import { Spinner, PlayerButtons } from "ui/components";
import formatDuration from "utils/formatDuration";
import normalizeCurrentTime from "utils/normalizeCurrentTime";
import { cx } from "utils/join-classnames";
import $ from "./styles.module.scss";

interface Props {
    videoUrl: string;
    thumbnailUrl: string;
    initialOffsetStart: number;
    initialOffsetEnd: number;
    trimMode?: AspectRatio;
    sizeMode?: "crop" | "resize";
    onChange(start: number, end: number, crop: Crop): void;
    blur?: boolean;
}
interface Dimensions {
    width: number;
    height: number;
}

const DEFAULT_FRAME_RATE = 25;

const createUserId = () => Math.ceil(Math.random() * 1e6);

const TrimPlayer: React.FC<Props> = ({
    videoUrl,
    thumbnailUrl,
    initialOffsetStart,
    initialOffsetEnd,
    trimMode,
    sizeMode,
    onChange,
    blur,
}) => {
    const [wasPlaying, setWasPlaying] = React.useState<boolean>(false);
    const [playing, setPlaying] = React.useState<boolean>(false);
    const [crop, setCrop] = React.useState<Crop>(new Crop());
    const [userInputId, setUserInputId] =
        React.useState<number>(createUserId());
    const [videoDuration, setVideoDuration] = React.useState<number>(0);
    const [currentTime, setCurrentTime] = React.useState<number>(0);
    const [videoSize, setVideoSize] = React.useState<Dimensions>({
        width: 1920,
        height: 1080,
    });
    const [offsetStart, setOffsetStart] =
        React.useState<number>(initialOffsetStart);
    const [offsetEnd, setOffsetEnd] = React.useState<number>(initialOffsetEnd);

    const videoRef =
        React.useRef<HTMLVideoElement>() as React.MutableRefObject<HTMLVideoElement>;

    const videoBlurRef =
        React.useRef<HTMLVideoElement>() as React.MutableRefObject<HTMLVideoElement>;
    const animationFrame = React.useRef<number | null>(null);

    const [dynamicVideoStyle, setDynamicVideoStyle] =
        React.useState<React.CSSProperties>({});

    let dynamicDurationStyle = {};

    const rewind = (play = false) => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;
        if (!video) {
            return;
        }
        video.currentTime = offsetStart;
        if (videoBlur) {
            videoBlur.currentTime = offsetStart;
        }
        setCurrentTime(offsetStart);
        if (play) {
            video.play();
            if (videoBlur) {
                videoBlur.play();
            }
            return;
        }
        video.pause();
        if (videoBlur) {
            videoBlur.pause();
        }
    };

    const playLastSeconds = (count = 2) => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;

        if (!video) {
            return;
        }
        const videoOffset = video.duration - offsetEnd;
        let startSecond = videoOffset - count;
        if (startSecond < offsetStart) {
            startSecond = offsetStart;
        }

        setCurrentTime(startSecond);
        video.currentTime = startSecond;
        video.play();
        if (videoBlur) {
            videoBlur.currentTime = startSecond;
            videoBlur.play();
        }
    };

    const moveFrame = (direction: "forward" | "backward") => {
        const video = videoRef.current;
        const videoBlur = videoBlurRef.current;
        const framerate = DEFAULT_FRAME_RATE;
        if (!video) {
            return;
        }
        video.pause();
        if (videoBlur) {
            videoBlur.pause();
        }
        if (direction === "forward") {
            const newTime = video.currentTime + 1 / +framerate;
            if (newTime > video.duration - offsetEnd) {
                let newOffsetEnd = video.duration - newTime;
                if (newOffsetEnd < 0) {
                    newOffsetEnd = 0;
                }
                setOffsetEnd(newOffsetEnd);
            }
            video.currentTime = newTime;
            if (videoBlur) {
                videoBlur.currentTime = newTime;
            }
        } else {
            let newTime = video.currentTime - 1 / +framerate;
            if (newTime < offsetStart) {
                if (newTime < 0) {
                    newTime = 0;
                }
                setOffsetStart(newTime);
            }
            video.currentTime = newTime;
            if (videoBlur) {
                videoBlur.currentTime = newTime;
            }
        }
    };

    const markIn = () => {
        setOffsetStart(currentTime);
    };

    const markOut = () => {
        setOffsetEnd(videoDuration - currentTime);
    };

    const onDraggingStart = () => {
        const videoEl = videoRef.current;
        const videoBlurEl = videoBlurRef.current;
        if (videoEl) {
            setWasPlaying(!videoEl.paused);
            if (wasPlaying) {
                videoEl.pause();
                if (videoBlurEl) {
                    videoBlurEl.pause();
                }
            }
        }
    };

    const onDraggingEnd = () => {
        const videoEl = videoRef.current;
        const videoBlurEl = videoBlurRef.current;
        if (videoEl && wasPlaying) {
            videoEl.play();
            if (videoBlurEl) {
                videoBlurEl.play();
            }
        }
    };

    const onDurationChange = (name: "dot" | "start" | "end", value: number) => {
        const videoEl = videoRef.current;
        const videoBlurEl = videoBlurRef.current;
        if (name === "dot") {
            setUserInputId(createUserId());
            setCurrentTime(value);
            if (videoEl) {
                videoEl.currentTime = value;
                if (videoBlurEl) {
                    videoBlurEl.currentTime = value;
                }
            }
        }
        if (name === "start") {
            setUserInputId(createUserId());
            setOffsetStart(value);
            if (videoEl) {
                videoEl.currentTime = value;
                if (videoBlurEl) {
                    videoBlurEl.currentTime = value;
                }
            }
        }
        if (name === "end") {
            setUserInputId(createUserId());
            setOffsetEnd(value);
            if (videoEl) {
                videoEl.currentTime = videoDuration - value;
                if (videoBlurEl) {
                    videoBlurEl.currentTime = videoDuration - value;
                }
            }
        }
    };

    const handlePlay = () => {
        const videoEl = videoRef.current;
        const blurEl = videoBlurRef.current;
        if (!videoEl) {
            return;
        }
        if (videoEl.paused) {
            if (
                normalizeCurrentTime(currentTime) >=
                normalizeCurrentTime(videoDuration - offsetEnd)
            ) {
                videoEl.currentTime = offsetStart;
            }
            videoEl.play();
            if (blurEl) {
                blurEl.play();
            }
            return;
        }
        videoEl.pause();
        if (blurEl) {
            blurEl.pause();
        }
    };

    const changeArea = (changedCrop: Crop) => {
        const videoEl = videoRef.current;
        if (!videoEl) {
            return;
        }
        // Unscaled sizes: Frame: 1280/720, video: 1240/720
        // Scaled sizes: Frame: 768/432, square: 432/432
        // Scaled size -> video size
        // Square -> video size -> desired size.
        // => 1240/(1240/1280*768)*432
        const { videoWidth } = videoEl;
        // Fix for BNR
        // TODO: This translation needs to happen in the changedCropming area
        // Step 1): True video size needs to be calculated and constrained
        // Step 2): Translation needs to be applied to 'video square' rather than
        //          the size of the player element itself
        const deltaXOffset = 0;
        const multiplierWidth =
            videoSize.width /
            ((videoSize.width / videoWidth) * videoEl.offsetWidth);
        const multiplierHeight =
            videoSize.height /
            ((videoSize.height / videoEl.videoHeight) * videoEl.offsetHeight);
        const realX =
            Math.floor(changedCrop.point.x * multiplierWidth) - deltaXOffset;
        const realY = Math.floor(changedCrop.point.y * multiplierHeight);
        const realWidth = Math.floor(changedCrop.size.width * multiplierWidth);
        const realHeight = Math.floor(
            changedCrop.size.height * multiplierHeight,
        );
        const outputSize = {
            width: Math.ceil(changedCrop.outputSize.width * multiplierWidth),
            height: Math.ceil(changedCrop.outputSize.height * multiplierHeight),
        };
        const newCrop = new Crop();
        newCrop.size = { width: realWidth, height: realHeight };
        newCrop.point = { x: realX, y: realY };
        newCrop.outputSize = outputSize;
        newCrop.length = videoEl.duration ? videoEl.duration : 0.0;

        // Last pass to ensure correctness of the crop
        newCrop.length = Number.parseFloat(newCrop.length.toFixed(2));
        newCrop.size.width -= newCrop.size.width % 2;
        newCrop.size.height -= newCrop.size.height % 2;
        newCrop.outputSize.width -= newCrop.outputSize.width % 2;
        newCrop.outputSize.height -= newCrop.outputSize.height % 2;
        setCrop(newCrop);
        setDynamicVideoStyle({});
        dynamicDurationStyle = {};
    };

    const handleResizeArea = (trim: Crop) => {
        if (sizeMode === "resize") {
            setDynamicVideoStyle({
                width: `${trim.size.width}px`,
                marginLeft: `${trim.point.x}px`,
            });
            const leftOffset = trim.size.width + trim.point.x - 60; // 60 = duration element width + margin
            dynamicDurationStyle = {
                left: `${leftOffset}px`,
                right: "auto",
            };
        }
    };

    const handleButtonClicked = (buttonType: PlayerButtonType): void => {
        switch (buttonType) {
            case "goToInPoint":
                rewind();
                break;
            case "goToOutPoint":
                playLastSeconds(0);
                break;
            case "moveFrameBackward":
                moveFrame("backward");
                break;
            case "moveFrameForward":
                moveFrame("forward");
                break;
            case "playFromStart":
                rewind(true);
                break;
            case "playLast2Seconds":
                playLastSeconds(2);
                break;
            case "setInPoint":
                markIn();
                break;
            case "setOutPoint":
                markOut();
                break;
            case "togglePlay":
                handlePlay();
                break;
            default:
                break;
        }
    };

    React.useEffect(() => {
        const videoEl = videoRef.current;
        if (!videoEl) {
            return;
        }
        videoEl.currentTime = currentTime;
    }, [userInputId]);

    React.useEffect(() => {
        onChange(offsetStart, offsetEnd, crop);
    }, [offsetStart, offsetEnd, crop, onChange]);

    React.useEffect(() => {
        const video = videoRef.current;
        if (!video) {
            return undefined;
        }
        const checkVideoStatus = () => {
            if (
                normalizeCurrentTime(video.currentTime) <
                normalizeCurrentTime(offsetStart)
            ) {
                video.currentTime = offsetStart;
            } else if (
                normalizeCurrentTime(video.currentTime) >
                normalizeCurrentTime(video.duration - offsetEnd)
            ) {
                if (video.paused === false) {
                    video.pause();
                } else {
                    video.currentTime = video.duration - offsetEnd;
                }
            }
        };
        checkVideoStatus();

        const onPlay = (): void => {
            setPlaying(true);
        };
        const onPause = (): void => {
            setPlaying(false);
        };
        const onTimeUpdate = (): void => {
            setCurrentTime(video.currentTime);
            checkVideoStatus();
        };
        const onLoadedMetadata = (): void => {
            setVideoDuration(video.duration);
            setVideoSize({
                width: video.videoWidth,
                height: video.videoHeight,
            });
        };

        const cleanup = () => {
            video.removeEventListener("play", onPlay);
            video.removeEventListener("pause", onPause);
            video.removeEventListener("timeupdate", onTimeUpdate);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
            if (animationFrame.current) {
                cancelAnimationFrame(animationFrame.current);
            }
        };

        video.addEventListener("play", onPlay);
        video.addEventListener("pause", onPause);
        video.addEventListener("timeupdate", onTimeUpdate);
        video.addEventListener("loadedmetadata", onLoadedMetadata);

        return () => cleanup();
    }, []);

    React.useEffect(() => {
        if (!trimMode) {
            setDynamicVideoStyle({});
            dynamicDurationStyle = {};
        }
    }, [trimMode]);

    return (
        <div className={$.container}>
            <div className={$.container__inner}>
                <div className={$.container__gradient} />
                <div
                    className={$.container__video__duration}
                    style={dynamicDurationStyle}
                >
                    {formatDuration(videoDuration - offsetEnd - offsetStart)}
                </div>
                <div
                    style={{ opacity: videoDuration === 0 ? 1.0 : 0.0 }}
                    className={$.container__overlay}
                >
                    <span className={$.container__overlay__spinner}>
                        <Spinner />
                    </span>
                    <span>Loading video…</span>
                </div>
                {trimMode && sizeMode === "crop" && (
                    <TrimmingArea
                        aspectRatio={trimMode}
                        onChange={changeArea}
                    />
                )}
                {trimMode && sizeMode === "resize" && (
                    <ResizeArea
                        trimArea={handleResizeArea}
                        aspectRatio={trimMode}
                    />
                )}
                {blur && (
                    <video
                        className={cx(
                            $.container__video,
                            $.container__video__blur,
                        )}
                        src={videoUrl}
                        ref={videoBlurRef}
                        muted
                    />
                )}
                <video
                    className={$.container__video}
                    ref={videoRef}
                    src={videoUrl}
                    style={dynamicVideoStyle}
                    poster={thumbnailUrl}
                />
            </div>
            <DurationBar
                className={$.container__durationBar}
                progress={currentTime}
                max={videoDuration}
                onDraggingStart={onDraggingStart}
                onDraggingEnd={onDraggingEnd}
                onChange={onDurationChange}
                startOffset={offsetStart}
                endOffset={offsetEnd}
                vidRef={videoRef}
            />
            <PlayerButtons
                onButtonClick={handleButtonClicked}
                playing={playing}
                trimButtons
                useShortcuts
            />
        </div>
    );
};
export default TrimPlayer;
